/* assets/wishlist.js
   FINAL (for your theme):
   - Wishlist localStorage
   - Wishlist page render via section_id=wishlist-card (extracts ONLY li.grid__item)
   - Heart toggle everywhere
   - Remove + Share
   - Move to cart:
       - adds first available variant
       - refreshes cart drawer fully (fixes empty-cart -> non-empty)
       - updates HEADER cart bubble count instantly via /cart.js (because you do NOT have a cart-icon-bubble section)
   - FIX: wishlist badge hidden when 0 (no "0" shown)
*/

(() => {
  const KEY = "dawn:wishlist:v2";
  const EVENT = "wishlist:changed";

  // ---------------------------
  // Storage
  // ---------------------------
  const safeJson = (raw) => {
    try { return JSON.parse(raw); } catch { return null; }
  };

  const read = () => {
    const arr = safeJson(localStorage.getItem(KEY));
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  };

  const write = (items) => {
    const clean = Array.from(new Set((items || []).filter(Boolean)));
    localStorage.setItem(KEY, JSON.stringify(clean));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { items: clean } }));
    return clean;
  };

  const removeHandle = (handle) => write(read().filter((h) => h !== handle));

  const toggleHandle = (handle) => {
    const items = read();
    if (items.includes(handle)) {
      return { items: write(items.filter((h) => h !== handle)), action: "removed" };
    }
    return { items: write([...items, handle]), action: "added" };
  };

  // ---------------------------
  // Toast
  // ---------------------------
  let toastEl = null;
  let toastTimer = null;

  const ensureToast = () => {
    if (toastEl) return toastEl;
    toastEl = document.createElement("div");
    toastEl.className = "wishlist-toast";
    toastEl.setAttribute("role", "status");
    toastEl.setAttribute("aria-live", "polite");
    document.body.appendChild(toastEl);
    return toastEl;
  };

  const showToast = (msg) => {
    const el = ensureToast();
    el.textContent = msg;
    el.classList.remove("is-visible");
    void el.offsetWidth;
    el.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => el.classList.remove("is-visible"), 1400);
  };

  // ---------------------------
  // Sync UI (hearts + wishlist count)
  // ---------------------------
  const setBtnState = (btn, active) => {
    btn.classList.toggle("is-active", !!active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
  };

  // ✅ FIXED: hide badge when 0 and don't show "0"
  const syncWishlistBadge = (items) => {
    const count = items.length;

    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      // If 0: hide and clear (prevents showing 0)
      if (count <= 0) {
        el.textContent = "";
        el.hidden = true;
        el.setAttribute("aria-hidden", "true");
        return;
      }

      // If >0: show and set text
      el.textContent = String(count);
      el.hidden = false;
      el.setAttribute("aria-hidden", "false");
    });
  };

  const sync = (root = document) => {
    const items = read();

    root.querySelectorAll("[data-wishlist-btn]").forEach((btn) => {
      const handle = btn.getAttribute("data-handle");
      if (!handle) return;
      setBtnState(btn, items.includes(handle));
    });

    // keep header badge correct
    syncWishlistBadge(items);
  };

  // ---------------------------
  // IMPORTANT: your header renders cart bubble ONLY when cart != empty.
  // So we must PATCH/CREATE it using /cart.js (no cart-icon-bubble section exists).
  // ---------------------------
  async function updateHeaderCartBubbleCount() {
    try {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { "Accept": "application/json" },
      });
      if (!res.ok) return;

      const cart = await res.json();
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink) return;

      let bubble = cartLink.querySelector(".cart-count-bubble");

      // Create bubble if missing and cart has items
      if (!bubble && cart.item_count > 0) {
        bubble = document.createElement("div");
        bubble.className = "cart-count-bubble";
        bubble.innerHTML = `
          <span aria-hidden="true"></span>
          <span class="visually-hidden"></span>
        `;
        cartLink.appendChild(bubble);
      }

      // Update / remove bubble
      if (bubble) {
        if (cart.item_count > 0) {
          const countSpan = bubble.querySelector("span[aria-hidden='true']");
          const srSpan = bubble.querySelector(".visually-hidden");
          if (countSpan) countSpan.textContent = cart.item_count < 100 ? String(cart.item_count) : "99+";
          if (srSpan) srSpan.textContent = `Cart (${cart.item_count})`;
        } else {
          bubble.remove();
        }
      }
    } catch (e) {}
  }

  // ---------------------------
  // FULL cart drawer refresh (fix empty->non-empty state)
  // Replaces entire <cart-drawer> innerHTML + classes from fresh section render
  // ---------------------------
  async function refreshDawnCartDrawer({ openDrawer = false } = {}) {
    const currentDrawer = document.querySelector("cart-drawer");
    const wasOpen = currentDrawer?.classList.contains("active");

    const res = await fetch("/?sections=cart-drawer", {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!res.ok) return;

    const sections = await res.json();
    if (!sections["cart-drawer"]) return;

    const doc = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
    const freshDrawer = doc.querySelector("cart-drawer");
    const liveDrawer = document.querySelector("cart-drawer");

    if (!freshDrawer || !liveDrawer) return;

    // Copy classes to switch is-empty/non-empty correctly
    liveDrawer.className = freshDrawer.className;

    // Replace internal markup
    liveDrawer.innerHTML = freshDrawer.innerHTML;

    // Rebind overlay close
    const overlay = liveDrawer.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", liveDrawer.close.bind(liveDrawer));

    if (window.WbsCountdownTimer) {
      window.WbsCountdownTimer.init(liveDrawer);
    }

    // Rebind cart icon click handler because your CartDrawer sets it in constructor
    if (typeof liveDrawer.setHeaderCartIconAccessibility === "function") {
      liveDrawer.setHeaderCartIconAccessibility();
    }

    // Open/keep open if needed
    if (openDrawer || wasOpen) {
      setTimeout(() => liveDrawer.classList.add("animate", "active"));
      document.body.classList.add("overflow-hidden");
    }
  }

  // ---------------------------
  // Wishlist page render (extract only LI)
  // ---------------------------
  const renderWishlistPage = async () => {
    const page = document.querySelector("[data-wishlist-page]");
    if (!page) return;

    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    if (!grid) return;

    const items = read();

    if (!items.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    const parts = await Promise.all(
      items.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=wishlist-card`, {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!res.ok) return "";
          const html = await res.text();

          const doc = new DOMParser().parseFromString(html, "text/html");
          const li = doc.querySelector("li.grid__item");
          return li ? li.outerHTML : "";
        } catch {
          return "";
        }
      })
    );

    grid.innerHTML = parts.filter(Boolean).join("");
    sync(page);
  };

  // ---------------------------
  // Share wishlist
  // ---------------------------
  const getShareUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("wl", read().join(","));
    return url.toString();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    }
  };

  const importFromUrlOnce = () => {
    const url = new URL(window.location.href);
    const wl = url.searchParams.get("wl");
    if (!wl) return;

    const handles = wl.split(",").map((s) => s.trim()).filter(Boolean);
    if (!handles.length) return;

    write(handles);

    url.searchParams.delete("wl");
    window.history.replaceState({}, "", url.toString());
    showToast("Wishlist loaded");
  };

  // ---------------------------
  // Move to cart
  // ---------------------------
  const getFirstAvailableVariantId = async (handle) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    });
    if (!res.ok) throw new Error("Product JSON fetch failed");

    const p = await res.json();
    const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
    if (!v) throw new Error("No variants found");
    return v.id;
  };

  const addToCart = async (variantId, qty = 1) => {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  };

  // ---------------------------
  // Click handlers
  // ---------------------------
  document.addEventListener("click", async (e) => {
    const btnHeart = e.target.closest("[data-wishlist-btn]");
    const btnRemove = e.target.closest("[data-wishlist-remove]");
    const btnMove = e.target.closest("[data-wishlist-move-to-cart]");
    const btnShare = e.target.closest("[data-wishlist-share]");

    // Heart toggle
    if (btnHeart) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnHeart.getAttribute("data-handle");
      if (!handle) return;

      const res = toggleHandle(handle);
      setBtnState(btnHeart, res.items.includes(handle));
      showToast(res.action === "added" ? "Added to wishlist" : "Removed from wishlist");
      return;
    }

    // Remove
    if (btnRemove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnRemove.getAttribute("data-handle");
      if (!handle) return;

      removeHandle(handle);
      showToast("Removed from wishlist");
      return;
    }

    // Move to cart
    if (btnMove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnMove.getAttribute("data-handle");
      if (!handle) return;

      const originalText = btnMove.textContent;
      btnMove.disabled = true;
      btnMove.textContent = "Adding...";

      try {
        const variantId = await getFirstAvailableVariantId(handle);
        await addToCart(variantId, 1);

        // ✅ refresh drawer (empty->non-empty fix)
        await refreshDawnCartDrawer({ openDrawer: true });

        // ✅ update header bubble instantly (your header doesn't render bubble when empty)
        await updateHeaderCartBubbleCount();

        // remove from wishlist
        removeHandle(handle);

        showToast("Moved to cart");
      } catch (err) {
        console.error(err);
        showToast("Could not add to cart");
      } finally {
        btnMove.disabled = false;
        btnMove.textContent = originalText;
      }
      return;
    }

    // Share
    if (btnShare) {
      e.preventDefault();
      const url = getShareUrl();
      const ok = await copyToClipboard(url);
      showToast(ok ? "Wishlist link copied" : "Copy failed");
      return;
    }
  });

  // ---------------------------
  // Boot / events
  // ---------------------------
  const boot = () => {
    importFromUrlOnce();
    sync(document);
    renderWishlistPage();
    // keep bubble accurate on load too
    updateHeaderCartBubbleCount();
  };

  document.addEventListener("DOMContentLoaded", boot);

  window.addEventListener(EVENT, () => {
    sync(document);
    renderWishlistPage();
  });

  document.addEventListener("shopify:section:load", (e) => {
    sync(e.target);
    renderWishlistPage();
    updateHeaderCartBubbleCount();
  });

  window.DawnWishlist = {
    read, write, toggleHandle, removeHandle,
    sync, renderWishlistPage,
    refreshDawnCartDrawer,
    updateHeaderCartBubbleCount,
  };
})();